import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Modal } from '@/components/Modal';
import { submitApplication, VENDOR_KIND_LABEL, VENDOR_KINDS, type VendorKind } from '@/lib/auth/session';
import { shrinkImage } from '@/lib/image';
import { TRADES } from '@/lib/trades';

/*
  협력사 등록 신청 — 파트너 페이지의 「협력업체 등록」과 갈래 칸 셋이 여는 폼(DOC-96 §2-1 · §9).

  비밀번호 칸이 없다. 계정은 본사가 승인한 뒤 초대 링크로 선다 — 「계정 소속은 배치가 아니다」.
  사업자등록증은 필수다(사용자 결정 2026-09-29). 파일 없이는 보내기가 눌리지 않는다.

  갈래(외주·구매·용역)는 업체의 속성이다. 첫 화면의 큰 칸을 눌러 들어오면 그 갈래가 미리 골라져 있고,
  「협력업체 등록」으로 들어오면 여기서 고른다.

  성공·실패 문장은 각각 하나다. 「이미 등록된 업체입니다」 같은 말은 등록 여부를 확인해 주는 통로가
  되므로 쓰지 않는다 — 서버도 같은 이유로 400 하나로만 답한다.

  ★ 창까지 이 파일이 그린다(2026-09-29). 「접수」 버튼이 제목 줄의 「닫기」 옆에 서야 하는데(사용자 지시 ·
    저장소 규칙 「모달 버튼은 제목 줄에 둔다」), 눌릴 수 있는지를 아는 것은 이 폼이다. 부르는 쪽이 창을
    들고 있으면 그 판단을 밖으로 흘려보내야 한다 — 창을 여기로 가져오는 편이 짧다.
*/

const CERT_ACCEPT = '.pdf,image/jpeg,image/png';
const MAX_CERT_BYTES = 10 * 1024 * 1024;

/** 제목 줄의 버튼이 이 폼을 제출한다 — 버튼이 form 밖에 있으므로 `form` 속성으로 잇는다. */
const FORM_ID = 'vendor-register-form';

export function RegisterForm({
  kind = null,
  onClose,
  onDone,
}: {
  kind?: VendorKind | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [vendorKind, setVendorKind] = useState<VendorKind | ''>(kind ?? '');
  const [name, setName] = useState('');
  const [bizNo, setBizNo] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [openedOn, setOpenedOn] = useState('');
  const [mainTrade, setMainTrade] = useState('');
  const [bizType, setBizType] = useState('');
  const [bizClass, setBizClass] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [note, setNote] = useState('');
  const [cert, setCert] = useState<File | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const bizDigits = bizNo.replace(/\D/g, '');
  const canSubmit =
    !pending &&
    vendorKind !== '' &&
    name.trim() !== '' &&
    bizDigits.length === 10 &&
    ceoName.trim() !== '' &&
    openedOn !== '' &&
    mainTrade !== '' &&
    contactName.trim() !== '' &&
    contactPhone.trim() !== '' &&
    contactEmail.trim() !== '' &&
    cert !== null;

  async function onPickCert(file: File | null) {
    setFailed(false);
    if (file === null) {
      setCert(null);
      return;
    }
    // 사진은 검측 사진과 같은 크기로 줄인다. PDF 는 그대로 — shrinkImage 가 이미지가 아니면 돌려준다.
    const picked = await shrinkImage(file);
    setCert(picked.size > MAX_CERT_BYTES ? null : picked);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    // canSubmit 이 갈래·파일을 이미 좁혔다(별칭 조건 좁히기) — 다시 묻지 않는다.
    if (!canSubmit || cert === null) return;
    setPending(true);
    setFailed(false);
    try {
      await submitApplication(
        {
          vendorKind,
          name: name.trim(),
          bizNo: bizDigits,
          ceoName: ceoName.trim(),
          openedOn,
          mainTrade,
          bizType: blankToNull(bizType),
          bizClass: blankToNull(bizClass),
          address: blankToNull(address),
          phone: blankToNull(phone),
          contactName: contactName.trim(),
          contactPhone: contactPhone.trim(),
          contactEmail: contactEmail.trim(),
          note: blankToNull(note),
        },
        cert,
      );
      onDone();
    }
    catch {
      setFailed(true);
    }
    finally {
      setPending(false);
    }
  }

  return (
    <Modal
      title="협력업체 등록 신청"
      onClose={onClose}
      maxWidth="32rem"
      actions={
        <>
          <button type="submit" form={FORM_ID} className="btn-primary" disabled={!canSubmit}>
            {pending ? '보내는 중입니다…' : '접수'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={pending}>
            닫기
          </button>
        </>
      }
    >
    <form id={FORM_ID} onSubmit={onSubmit}>
      <Field label="갈래" required>
        <select
          className="input-field"
          value={vendorKind}
          onChange={(e) => setVendorKind(e.target.value as VendorKind | '')}
          required
        >
          <option value="">선택</option>
          {VENDOR_KINDS.map((it) => (
            <option key={it} value={it}>
              {VENDOR_KIND_LABEL[it]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="상호" required>
        <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" required />
      </Field>
      <Field label="사업자등록번호" required>
        <input
          className="input-field"
          value={bizNo}
          onChange={(e) => setBizNo(e.target.value)}
          inputMode="numeric"
          placeholder="123-45-67890"
          required
        />
      </Field>
      <Field label="대표자" required>
        <input className="input-field" value={ceoName} onChange={(e) => setCeoName(e.target.value)} required />
      </Field>
      <Field label="개업일자" required>
        <input className="input-field" type="date" value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} required />
      </Field>
      <Field label="대표 공종" required>
        <select className="input-field" value={mainTrade} onChange={(e) => setMainTrade(e.target.value)} required>
          <option value="">선택</option>
          {TRADES.map((trade) => (
            <option key={trade} value={trade}>
              {trade}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="업태">
          <input className="input-field" value={bizType} onChange={(e) => setBizType(e.target.value)} placeholder="건설업" />
        </Field>
        <Field label="종목">
          <input className="input-field" value={bizClass} onChange={(e) => setBizClass(e.target.value)} placeholder="건축공사" />
        </Field>
      </div>
      <Field label="회사 주소">
        <input className="input-field" value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
      </Field>
      <Field label="회사 전화">
        <input className="input-field" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
      </Field>

      <Field label="담당자 이름" required>
        <input className="input-field" value={contactName} onChange={(e) => setContactName(e.target.value)} autoComplete="name" required />
      </Field>
      <Field label="담당자 휴대전화" required>
        <input
          className="input-field"
          type="tel"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          inputMode="tel"
          autoComplete="tel"
          required
        />
      </Field>
      <Field label="담당자 이메일" required>
        <input
          className="input-field"
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          inputMode="email"
          autoComplete="email"
          required
        />
      </Field>

      <Field label="사업자등록증" required>
        <input
          className="input-field"
          type="file"
          accept={CERT_ACCEPT}
          onChange={(e) => void onPickCert(e.target.files?.[0] ?? null)}
          required
        />
      </Field>

      <Field label="주요 실적 · 하고 싶은 말">
        <textarea className="input-field" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {/* 실패는 본문 끝에 남긴다 — 제목 줄의 버튼 옆에 쓰면 긴 폼에서 글자가 밀린다. */}
      {failed && (
        <p className="text-ui-xs" style={{ color: 'var(--danger)' }}>
          접수하지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      )}
    </form>
    </Modal>
  );
}

/* 로그인 칸과 같은 모양의 라벨. 손가락으로 누르는 화면이라 라벨까지 눌리게 label 로 감싼다. */
function Field({ label, required = false, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
        {label}
        {required && <span style={{ color: 'var(--danger)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
